import * as React from 'react';
import { PaymentDTO, Res } from 'core/api';
import * as UI from 'core/ui';
import { SessionProvider } from 'core/infrastructure';

export type PaymentDirection = 'PAY' | 'REFUND';
export type TransactionType = 'Pay' | 'PrePay' | 'Refund' | 'PreRefund' | 'AddTip' | 'RemoveTip' | 'NoDocument';

export type DeviceType = 'CashControl' | 'CardProcessor'
export type CardTransaction = { device: 'CardProcessor'; amount: number; type: TransactionType, xmlDetails: string };
export type CashTransaction = { device: 'CashControl', amount: number, type: TransactionType }
export type Transaction = CardTransaction | CashTransaction;

export function isExternalTransaction(transaction: Transaction): boolean {
	return transaction.device === 'CardProcessor' && SessionProvider.getCurrent().UseCardProcessor
		|| transaction.device === 'CashControl' && SessionProvider.getCurrent().UseCashControl;
}


export function getReversalTransactionType(type: TransactionType): TransactionType {
	switch (type) {
		case 'Pay': return 'Refund';
		case 'PrePay': return 'PreRefund';
		case 'Refund': return 'Pay';
		case 'PreRefund': return 'PrePay';
		case 'AddTip': return 'RemoveTip';
		case 'RemoveTip': return 'AddTip';
		default: return 'NoDocument';
	}
}

export function paymentRequiresCardProcessor(payment: PaymentDTO) {
	// Miramos que el importe o la propina sea distinto de cero. Hay casos en los que nos pueden pasar un pago en tarjeta con 0€, por ejemplo
	// al crear un pedido o un albarán, y eso hace que luego falle si intentamos registar un pago de 0€ con una pasarela de pago. Esto es algo que
	// no debería pasar porque al crear un pedido o un albarán la forma de pago por defecto es efectivo, que si es de 0€ no pasa nada.
	return payment.UseCardProcessor && SessionProvider.getCurrent().UseCardProcessor && (payment.Amount !== 0 || payment.TipAmount !== 0);
}

export function paymentRequiresCashControl(payment: PaymentDTO) {
	return payment.IsCash && SessionProvider.getCurrent().UseCashControl && payment.Amount !== 0;
}

export function getTransactionAmount(payment: PaymentDTO): number {
	// En los pagos con tarjeta siempre metemos la propina
	return paymentRequiresCardProcessor(payment) ? payment.Amount + payment.TipAmount : payment.Amount;
}

export function deviceDescription(device: DeviceType): string {
	switch (device) {
		case 'CardProcessor': return Res.Msg.MSG_EXTERNAL_PAYMENT_CARD();
		case 'CashControl': return Res.Msg.MSG_EXTERNAL_PAYMENT_CASH();
	}
}

export function TransactionTable({ externalTransactions }: { externalTransactions: Transaction[] }) {
	return <table className='table table-condensed table-striped text-16'>
		<thead>
			<tr>
				<th>{Res.Web.EXTERNAL_PAYMENT_TRANSACTION_TYPE_CLM()}</th>
				<th className='text-right'>{Res.Web.EXTERNAL_PAYMENT_TRANSACTION_AMOUNT_CLM()}</th>
			</tr>
		</thead>
		<tbody>
			{externalTransactions.map((tx, idx) => <tr key={idx}>
				<td>{deviceDescription(tx.device)}</td>
				<td className='text-right'>{UI.Format.asMoney(Math.abs(tx.amount))}</td>
			</tr>)}
		</tbody>
	</table>;
}

export function assertAreExternalTransactions(...externalTransactions: Transaction[]) {
	if (externalTransactions.some(x => !isExternalTransaction(x)))
		throw Error('Sólo deberían intentar mostrarse transacciones con dispositivos externos');
}
